import {
	Component,
	disableAutoStart,
	EventListener,
	triggerPublicEvent,
	startComponents,
	stopComponents,
} from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { Backdrop, IBackdropTrigger } from '../../../atoms/backdrop/js/backdrop';
import createFocusTrap, { FocusTrap } from 'focus-trap';
import { getCurrentViewport } from '@gondel/plugin-media-queries';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';
import { IGScrollHandlerOptions } from '@swisslife/shared-types/src/types/gondel-public-events';

export type AnyFunction = () => any | never;

export interface IOverlayHookMap {
	[hook: string]: AnyFunction[];
}

export interface IOverlayLifecycleHookMap {
	[hook: string]: AnyFunction[];
}

export interface IOverlayOptions {
	component?: string;
	modifiers?: string[];
}

enum States {
	OverlayVisible = 'state-o-overlay--visible',
	OverlayTransition = 'state-o-overlay--transition',
	FocusTrapActive = 'state-o-overlay--focus-trap-active',
	Fetching = 'state-o-overlay--fetching',
	FetchError = 'state-o-overlay--fetch-error',
	NoScroll = 'state-o-overlay--no-scroll',
}

enum Selectors {
	Wrapper = '.js-o-overlay__wrapper',
	Content = '.js-o-overlay-content',
	Close = '.js-o-overlay__close',
	Interaction = '.js-o-overlay-footer__interaction',
	Title = '.js-o-overlay__title',
	ErrorMessage = '.js-o-overlay-content__error-message',
	Loader = '.js-o-overlay-content__loader',
	Header = '.js-o-overlay-header',
}

export enum Modifier {
	Form = 'form',
}

export enum LifecycleHook {
	hide = '@@overlay/lifecycle/hide',
	show = '@@overlay/lifecycle/show',
}

/**
 * Basic Overlay implementation, handles all events and triggers
 * from the markup.
 *
 * @author jbiasi
 * @version 1.0
 */
@Component('Overlay')
export class Overlay extends GondelJqueryComponent {
	private $content: JQuery<HTMLElement>;
	private $loader: JQuery;
	private $errorMessage: JQuery;
	private visible: boolean = false;
	private backdrop: Backdrop;
	private trap: FocusTrap;
	private interactionHooks: IOverlayHookMap = {};
	private lifecycleHooks: IOverlayLifecycleHookMap = {};
	private shouldFetchContent: boolean;
	private fetchContentUrl: string;

	public get isVisible(): boolean {
		return this.visible;
	}

	public start() {
		this.visible = this.$ctx.hasClass(States.OverlayVisible);
		this.backdrop = Backdrop.getComponent();
		this.$content = this.$ctx.find(Selectors.Content);
		this.$loader = this.$ctx.find(Selectors.Loader);
		this.$errorMessage = this.$ctx.find(Selectors.ErrorMessage);
		this.shouldFetchContent = this.$ctx.data('fetch-content') || false;

		this.trap = createFocusTrap(this.$ctx.get(0), {
			clickOutsideDeactivates: true,
			escapeDeactivates: true,
			onActivate: () => {
				this.$ctx.addClass(States.FocusTrapActive);
			},
			onDeactivate: () => {
				this.$ctx.removeClass(States.FocusTrapActive);
			},
		});

		if (this.isVisible) {
			this.trap.activate();

			// if the overlay is initially open, we override the default backdrop onInteraction method
			const backdropTrigger: IBackdropTrigger = this.backdrop.currentBackdropTrigger;

			if (backdropTrigger) {
				backdropTrigger.onInteraction = (ev: string) => {
					// hide the overlay on backdrop click
					if (ev === 'click') {
						this.hide();
					}
				};
			}

			triggerPublicEvent('gScrollHandler', this, undefined, { scroll: false } as IGScrollHandlerOptions);
		} else if (this.shouldFetchContent) {
			this.onLifecycleHook(LifecycleHook.show, () => {
				this.$ctx.addClass(States.Fetching);
				this.fetchContent();
			});

			this.onLifecycleHook(LifecycleHook.hide, () => {
				this.fetchContentUrl = '';
				this.inject(this.$loader, true);
			});
		}
	}

	public addModifier(modifier: Modifier) {
		this.$ctx.addClass(`o-overlay--${modifier}`);
	}

	public removeModifier(modifier: Modifier) {
		this.$ctx.removeClass(`o-overlay--${modifier}`);
	}

	public show(opts?: IOverlayOptions) {
		this._setOverlayVisibilityState(true, opts);
		this._triggerLifecycleHook(LifecycleHook.show);
	}

	public inject($node: JQuery, clear: boolean = false) {
		if (clear) {
			this.teardownContent(this.$content);
			this.$content.empty();
		}

		$node.clone().appendTo(this.$content);

		this.bootstrapContent(this.$content);
	}

	public setTitle(title: string) {
		const $title = this.$ctx.find(Selectors.Title);

		if ($title.length > 0) {
			$title.text(title);
		}
	}

	public setFetchContentUrl(contentUrl: string) {
		this.fetchContentUrl = contentUrl;
	}

	public setScrolling(newState: boolean) {
		if (newState) {
			this.$ctx.removeClass(States.NoScroll);
		} else {
			this.$ctx.addClass(States.NoScroll);
		}
	}

	public scrollToTarget($target: JQuery<HTMLElement>) {
		const currentVp = getCurrentViewport();

		let $scrollCtx = this.$content;

		// for the small viewports we scroll the wrapper div
		if (currentVp === 'small' || currentVp === 'xsmall' || currentVp === 'xxsmall') {
			$scrollCtx = this.$ctx.find(Selectors.Wrapper);
		}
		const scrollPosition = this.getDistanceToScrollableDiv($target, $scrollCtx);
		$scrollCtx.animate({ scrollTop: scrollPosition }, 500);
	}

	public hide() {
		this._setOverlayVisibilityState(false);
		this._triggerLifecycleHook(LifecycleHook.hide);
	}

	public onInteractionHook(event: string, hook: AnyFunction) {
		if (!Array.isArray(this.interactionHooks[event])) {
			this.interactionHooks[event] = [];
		}

		this.interactionHooks[event].push(hook);
	}

	public onLifecycleHook(event: LifecycleHook, hook: AnyFunction) {
		if (!Array.isArray(this.lifecycleHooks[event])) {
			this.lifecycleHooks[event] = [];
		}

		this.lifecycleHooks[event].push(hook);
	}

	@EventListener('click', Selectors.Close)
	public _handleCloseClick() {
		this.hide();
	}

	@EventListener('click', Selectors.Interaction)
	public _handleInteractionClick(ev: MouseEvent) {
		if (ev.target) {
			const $target = $(ev.target);
			const actionType = $target.data('action-type') || '';
			const interactionHooks = this.interactionHooks[actionType] || [];

			interactionHooks.forEach((hook: AnyFunction) => {
				hook.apply($target, [ev, $target, actionType]);
			});
		}
	}

	private _triggerLifecycleHook(event: LifecycleHook): any | void {
		return (this.lifecycleHooks[event] || []).map(
			(hook: AnyFunction): any => {
				return hook();
			}
		);
	}

	private _setOverlayVisibilityState(state: boolean, opts?: IOverlayOptions) {
		if (this.visible === state) {
			return;
		}

		this.visible = state;
		this._handleStateChange(opts);
	}

	private _handleStateChange(opts?: IOverlayOptions) {
		if (this.visible) {
			// show backdrop with options
			this.backdrop.show({
				component: opts ? `Overlay${opts.component ? `(${opts.component})` : ''}` : 'Overlay',
				modifiers: opts && opts.modifiers ? opts.modifiers : [],
				onInteraction: (ev: string) => {
					// hide the overlay on backdrop click
					if (ev === 'click') {
						this.hide();
					}
				},
			});

			this.$ctx.addClass(States.OverlayVisible);
			this.$ctx.outerWidth(); // Reflow to make transition work
			this.$ctx.addClass(States.OverlayTransition);

			const eventOptions: IGScrollHandlerOptions = {
				scroll: false,
			};

			triggerPublicEvent('gScrollHandler', this, undefined, eventOptions);

			this.$ctx.one('transitionend', () => {
				this.trap.activate();
			});
		} else {
			this.trap.deactivate();

			this.$ctx.removeClass(States.OverlayTransition);

			this.$ctx.one('transitionend', () => {
				this.$ctx.removeClass(States.OverlayVisible);

				const eventOptions: IGScrollHandlerOptions = {
					scroll: true,
				};

				triggerPublicEvent('gScrollHandler', this, undefined, eventOptions);
				this.backdrop.hide();
			});
		}
	}

	private async fetchContent() {
		let $content: JQuery;

		try {
			// fetch but wait for 150 ms minimum (prevent too fast update / ui flickering)
			const fragment = (await Promise.all([ajax<string>(this.fetchContentUrl), this.wait(150)]))[0];
			$content = $(fragment);
		} catch (error) {
			// we take the errorMessage from the markup and replace it (via inject) to remove the loader
			$content = this.$errorMessage;
			this.$ctx.addClass(States.FetchError);
		}

		this.$ctx.removeClass(States.Fetching);

		this.inject($content, true);
	}

	private wait(delay: number) {
		return new Promise((receive) => setTimeout(receive, delay));
	}

	/**
	 * Calculation is inspired by https://github.com/flesler/jquery.scrollTo/blob/master/jquery.scrollTo.js
	 * Returns the vertical distance between $target and top of the scrollable div of the overlay
	 */
	private getDistanceToScrollableDiv = ($target: JQuery<HTMLElement>, $scrollCtx: JQuery<HTMLElement>): number => {
		return $target.offset()!.top + ($scrollCtx.scrollTop()! - $scrollCtx.offset()!.top);
	};

	private hasOnetrustWraps($content) {
		const $onetrustWraps = $content.find('.js-o-onetrust-wrapper');
		return $onetrustWraps.length > 0;
	}

	// onetrust-wrapper helper: bootstrap for overlay content in otw (required for form f.e.)
	private async bootstrapContent($content) {
		if (this.hasOnetrustWraps($content)) {
			// disable gondel auto start for default name space
			disableAutoStart();

			// start onetrustWrap first
			await startComponents($content[0], 't');
		}
		// then wrapped component
		await startComponents($content[0]);
	}

	// onetrust-wrapper helper: stop components in overlay content in otw (required for form f.e.)
	private teardownContent($content) {
		// stop content component first
		stopComponents($content[0]);
		if (this.hasOnetrustWraps($content)) {
			// then stop onetrustWrap
			stopComponents($content[0], 't');
		}
	}
}
